import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { Mail, MessageSquareText, Phone, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { CallAPIGetPromise } from "Common/commaon";
import "./insurance-contacts.css";

type InsuranceContact = {
  id: number | string;
  name: string;
  email: string;
  phone?: string | null;
  subject?: string | null;
  message: string;
  created_at?: string | null;
};

const PAGE_SIZE = 20;

function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatPhone(value?: string | null) {
  if (!value) return "—";

  const digits = value.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return value;

  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

function normalizeContactsResponse(response: unknown): InsuranceContact[] {
  if (Array.isArray(response)) return response as InsuranceContact[];

  if (response && typeof response === "object") {
    const body = response as { data?: unknown };
    if (Array.isArray(body.data)) return body.data as InsuranceContact[];
    if (body.data && typeof body.data === "object") {
      const nested = body.data as { data?: unknown };
      if (Array.isArray(nested.data)) return nested.data as InsuranceContact[];
    }
  }

  return [];
}

export default function InsuranceContactsAdmin() {
  const [contacts, setContacts] = useState<InsuranceContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const loadContacts = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const response = await CallAPIGetPromise("/misc/getInsuranceContacts");
      setContacts(normalizeContactsResponse(response));
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load insurance contacts. Please try again."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Insurance Contacts";
    void loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const filteredContacts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return contacts;

    return contacts.filter((contact) =>
      [contact.name, contact.email, contact.phone, contact.subject, contact.message]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [contacts, search]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visibleContacts = filteredContacts.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <main className="page-content insurance-contacts-page">
      {error ? (
        <Alert variant="danger" className="insurance-contacts-alert">
          <div>
            <strong>Contacts could not be loaded.</strong>
            <span>{error}</span>
          </div>
          <Button variant="outline-danger" size="sm" onClick={() => void loadContacts()}>
            Try again
          </Button>
        </Alert>
      ) : null}

      <section className="insurance-contacts-summary" aria-label="Insurance contact summary">
        <Card>
          <Card.Body>
            <span className="insurance-contacts-summary__icon" aria-hidden="true">
              <ShieldCheck />
            </span>
            <div>
              <span>Total inquiries</span>
              <strong>{contacts.length.toLocaleString()}</strong>
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <span className="insurance-contacts-summary__icon" aria-hidden="true">
              <MessageSquareText />
            </span>
            <div>
              <span>Matching results</span>
              <strong>{filteredContacts.length.toLocaleString()}</strong>
            </div>
          </Card.Body>
        </Card>
      </section>

      <Card className="insurance-contacts-card">
        <Card.Header className="insurance-contacts-toolbar">
          <div>
            <h2>Insurance inquiries</h2>
            <p>Contact requests submitted through the insurance inquiry form.</p>
          </div>
          <div className="insurance-contacts-actions">
            <label className="insurance-contacts-search">
              <span className="visually-hidden">Search insurance contacts</span>
              <div className="app-search-field">
                <Search className="app-field-icon" aria-hidden="true" />
                <Form.Control
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search name, email, subject..."
                />
              </div>
            </label>
            <Button
              variant="light"
              onClick={() => void loadContacts(true)}
              disabled={loading || refreshing}
            >
              {refreshing ? (
                <Spinner size="sm" role="status" aria-label="Refreshing contacts" />
              ) : (
                <RefreshCw aria-hidden="true" />
              )}
              Refresh
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="visually-hidden" role="status">
              Loading insurance contacts...
            </div>
          ) : null}
          <div className="table-responsive">
            <Table hover className="insurance-contacts-table align-middle mb-0" aria-label="Insurance contact inquiries">
              <thead>
                <tr>
                  <th scope="col">Contact</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Message</th>
                  <th scope="col">Received</th>
                </tr>
              </thead>
              <tbody aria-live="polite" aria-busy={loading}>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`insurance-contact-skeleton-${index}`} className="insurance-contact-skeleton">
                      <td><span /><small /></td>
                      <td><span /></td>
                      <td><span /></td>
                      <td><span /><small /></td>
                      <td><span /></td>
                    </tr>
                  ))
                ) : visibleContacts.length ? (
                  visibleContacts.map((contact) => (
                    <tr key={contact.id}>
                      <td>
                        <strong>{contact.name || "—"}</strong>
                        <a href={`mailto:${contact.email}`}>
                          <Mail aria-hidden="true" />
                          {contact.email || "—"}
                        </a>
                      </td>
                      <td>
                        {contact.phone ? (
                          <a href={`tel:${contact.phone}`}>
                            <Phone aria-hidden="true" />
                            {formatPhone(contact.phone)}
                          </a>
                        ) : "—"}
                      </td>
                      <td>{contact.subject || "General inquiry"}</td>
                      <td><p className="insurance-contact-message">{contact.message || "—"}</p></td>
                      <td className="insurance-contact-date">{formatDate(contact.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <div className="insurance-contacts-empty">
                        <MessageSquareText aria-hidden="true" />
                        <strong>{search ? "No matching inquiries" : "No insurance inquiries yet"}</strong>
                        <span>
                          {search
                            ? "Try a different name, email, phone number, or subject."
                            : "New insurance contact requests will appear here."}
                        </span>
                        {search ? (
                          <Button variant="light" size="sm" onClick={() => setSearch("")}>
                            Clear search
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>

        {!loading && filteredContacts.length > 0 ? (
          <Card.Footer className="insurance-contacts-pagination">
            <span>
              Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredContacts.length)} of{" "}
              {filteredContacts.length}
            </span>
            <div>
              <Button
                size="sm"
                variant="light"
                disabled={safePage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                Previous
              </Button>
              <span>Page {safePage} of {totalPages}</span>
              <Button
                size="sm"
                variant="light"
                disabled={safePage === totalPages}
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              >
                Next
              </Button>
            </div>
          </Card.Footer>
        ) : null}
      </Card>
    </main>
  );
}
